import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { liveVisitorsApi } from '../../api/endpoints/liveVisitors';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { DollarSign, ShoppingCart, Users, TrendingUp, Eye, BarChart3, UserCheck, TrendingDown, ArrowLeftRight, Search, Smartphone, Monitor, Bot, Globe, UserPlus, UserCog, FileText, Clock } from 'lucide-react';
import { analyticsApi } from '../../api/endpoints/analytics';
import { StatCard } from '../../components/ui/StatCard';
import { Button } from '../../components/ui/Button';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { formatCurrency, formatCountryName } from '../../lib/formatters';
import { CurrencyDisplay } from '../../components/ui/CurrencyDisplay';
import { STATUS_COLORS } from '../../lib/constants';
import { useStore } from '../../contexts/StoreContext';

const PERIODS = ['7d', '30d', '90d', '12m'] as const;
type ChartTab = 'revenue' | 'orders' | 'visits';

export function AnalyticsPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { currentStore } = useStore();
  const [period, setPeriod] = useState<string>('30d');
  const [compareMode, setCompareMode] = useState(false);
  const [chartTab, setChartTab] = useState<ChartTab>('revenue');

  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics', storeId, period],
    queryFn: () => analyticsApi.getAnalytics(storeId!, period),
    enabled: !!storeId,
    refetchInterval: 60 * 1000, // Refresh aggregate stats every minute
  });

  // Subscribe to live SSE for real-time Active Now count
  const [liveActive, setLiveActive] = useState<number | null>(null);
  useEffect(() => {
    if (!storeId) return;
    const es = new EventSource(liveVisitorsApi.getStreamUrl(storeId), { withCredentials: true });
    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'active_count') setLiveActive(msg.count ?? 0);
      } catch { /* ignore */ }
    };
    return () => es.close();
  }, [storeId]);

  if (isLoading) return <LoadingSpinner fullPage />;

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-text-muted">{t('analytics.loadError', 'Failed to load analytics data.')}</p>
      </div>
    );
  }

  const stats = data?.stats;
  const timeSeries = data?.timeSeries ?? [];
  const topProducts = data?.topProducts ?? [];
  const topCustomers = data?.topCustomers ?? [];
  const categoryBreakdown = data?.categoryBreakdown ?? [];
  const paymentMethodBreakdown = data?.paymentMethodBreakdown ?? [];
  const traffic = data?.traffic;
  const trafficSources = data?.trafficSources ?? [];
  const deviceBreakdown = data?.deviceBreakdown ?? [];
  const countryBreakdown = data?.countryBreakdown ?? [];
  const conversionFunnel = data?.conversionFunnel;
  const orderStatusBreakdown = data?.orderStatusBreakdown ?? [];
  const customers = data?.customers ?? { total: 0, new: 0, returning: 0 };
  const topPages = data?.topPages ?? [];
  const activeVisitors = liveActive ?? data?.activeVisitors ?? 0;
  const hourlyVisits = data?.hourlyVisits ?? [];
  const last24hVisits = data?.last24hVisits ?? null;

  // Check if visits data is available in the timeSeries
  const hasVisitsData = timeSeries.some((d) => d.visits != null && d.visits > 0);

  function calcTrend(current: number | undefined, previous: number | undefined) {
    if (!compareMode || current == null || previous == null || previous === 0) return undefined;
    const change = ((current - previous) / previous) * 100;
    return { value: Math.round(change * 10) / 10, label: t('analytics.vsPrevious') };
  }

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold">{t('sidebar.analytics')}</h1>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 rounded-lg bg-bg-secondary border border-border-default p-1">
            {PERIODS.map((p) => (
              <Button
                key={p}
                variant={period === p ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setPeriod(p)}
              >
                {t(`analytics.${p}`)}
              </Button>
            ))}
          </div>
          <Button
            variant={compareMode ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setCompareMode(!compareMode)}
          >
            <ArrowLeftRight className="h-4 w-4 me-1" />
            {t('analytics.compare')}
          </Button>
        </div>
      </div>

      {/* Realtime Bar */}
      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-border-default bg-bg-secondary px-4 py-3 shadow-sm">
        <span className="h-2 w-2 rounded-full bg-success animate-pulse shrink-0" />
        <span className="text-sm font-medium text-text-secondary">
          {t('analytics.activeNow', 'Active Now')}
        </span>
        <span className="text-lg font-bold">{activeVisitors}</span>
        <span className="text-xs text-text-muted">
          {t('analytics.visitors', 'visitors')}
        </span>

        {/* Realtime sparkline */}
        {hourlyVisits.length > 0 && (
          <div className="ms-2 shrink-0" style={{ width: 200, height: 40 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyVisits} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
                <Tooltip
                  cursor={false}
                  contentStyle={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border-default)',
                    borderRadius: 6,
                    fontSize: 11,
                    padding: '4px 8px',
                  }}
                  labelFormatter={(label: string) => label?.substring?.(11, 16) ?? label}
                  formatter={(value: number) => [value, t('analytics.visitors', 'visitors')]}
                />
                <Bar
                  dataKey="visits"
                  fill="var(--color-success)"
                  opacity={0.6}
                  radius={[2, 2, 0, 0]}
                  maxBarSize={8}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* 24h visit summary */}
        {(last24hVisits != null || (traffic && traffic.pageViews != null)) && (
          <div className="ms-auto flex items-center gap-1.5 text-xs text-text-muted">
            <Clock className="h-3 w-3" />
            <span>
              {t('analytics.last24h', '24h')}:{' '}
              <strong className="text-text-primary">
                {(last24hVisits ?? traffic?.pageViews ?? 0).toLocaleString()}
              </strong>{' '}
              {t('analytics.visits', 'visits')}
            </span>
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <StatCard
          title={t('dashboard.revenue')}
          value={stats?.revenue ?? 0}
          currency={currentStore?.currency || 'SAR'}
          icon={<DollarSign className="h-5 w-5" />}
          trend={calcTrend(stats?.revenue, stats?.previousRevenue)}
        />
        <StatCard
          title={t('dashboard.orders')}
          value={stats?.orders ?? 0}
          icon={<ShoppingCart className="h-5 w-5" />}
          trend={calcTrend(stats?.orders, stats?.previousOrders)}
        />
        <StatCard
          title={t('dashboard.customers')}
          value={stats?.customers ?? 0}
          icon={<Users className="h-5 w-5" />}
          trend={calcTrend(stats?.customers, stats?.previousCustomers)}
        />
        <StatCard
          title={t('dashboard.avgOrder')}
          value={stats?.averageOrderValue ?? 0}
          currency={currentStore?.currency || 'SAR'}
          icon={<TrendingUp className="h-5 w-5" />}
          trend={calcTrend(stats?.averageOrderValue, stats?.previousAverageOrderValue)}
        />
      </div>

      {/* Secondary KPIs - conversion rate + views (if available from API) */}
      {(stats?.conversionRate != null || stats?.views != null) && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          {stats?.views != null && (
            <StatCard
              title={t('analytics.views')}
              value={stats.views}
              icon={<Eye className="h-5 w-5" />}
            />
          )}
          {stats?.conversionRate != null && (
            <StatCard
              title={t('analytics.conversionRate')}
              value={`${stats.conversionRate.toFixed(1)}%`}
              icon={<BarChart3 className="h-5 w-5" />}
            />
          )}
        </div>
      )}

      {/* Traffic Stats */}
      {traffic && (
        <div className="grid gap-4 sm:grid-cols-3 mb-6">
          <StatCard
            title={t('analytics.pageViews')}
            value={traffic.pageViews.toLocaleString()}
            icon={<Eye className="h-5 w-5" />}
          />
          <StatCard
            title={t('analytics.uniqueVisitors')}
            value={traffic.uniqueVisitors.toLocaleString()}
            icon={<UserCheck className="h-5 w-5" />}
          />
          <StatCard
            title={t('analytics.bounceRate')}
            value={`${traffic.bounceRate.toFixed(1)}%`}
            icon={<TrendingDown className="h-5 w-5" />}
          />
        </div>
      )}

      {/* Charts Section with Tab Selector */}
      {timeSeries.length > 0 && (
        <div className="rounded-xl border border-border-default bg-bg-secondary p-5 shadow-sm mb-6">
          {/* Chart tab selector */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">
              {chartTab === 'revenue' && t('analytics.salesOverTime', 'Sales Over Time')}
              {chartTab === 'orders' && t('analytics.ordersOverTime', 'Orders Over Time')}
              {chartTab === 'visits' && t('analytics.visitsOverTime', 'Visits Over Time')}
            </h2>
            <div className="flex gap-1 rounded-lg bg-bg-tertiary p-1">
              <button
                onClick={() => setChartTab('revenue')}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  chartTab === 'revenue'
                    ? 'bg-accent text-white shadow-sm'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                }`}
              >
                {t('dashboard.revenue', 'Revenue')}
              </button>
              <button
                onClick={() => setChartTab('orders')}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  chartTab === 'orders'
                    ? 'bg-[#3b82f6] text-white shadow-sm'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                }`}
              >
                {t('dashboard.orders', 'Orders')}
              </button>
              {hasVisitsData && (
                <button
                  onClick={() => setChartTab('visits')}
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                    chartTab === 'visits'
                      ? 'bg-[#8b5cf6] text-white shadow-sm'
                      : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                  }`}
                >
                  {t('analytics.visits', 'Visits')}
                </button>
              )}
            </div>
          </div>

          {/* Revenue bar chart */}
          {chartTab === 'revenue' && (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={timeSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="analyticsBarGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-accent)" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="var(--color-accent)" stopOpacity={0.4} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-default)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v: string) => v.slice(5)}
                  tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                  axisLine={{ stroke: 'var(--color-border-default)' }}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v: number) => formatCurrency(v, currentStore?.currency)}
                  tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                  axisLine={false}
                  tickLine={false}
                  width={72}
                />
                <Tooltip
                  cursor={{ fill: 'var(--color-bg-hover)', opacity: 0.4 }}
                  contentStyle={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border-default)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelFormatter={(label: string) => label}
                  formatter={(value: number) => [formatCurrency(value, currentStore?.currency), t('dashboard.revenue')]}
                />
                <Bar
                  dataKey="revenue"
                  fill="url(#analyticsBarGradient)"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={48}
                />
              </BarChart>
            </ResponsiveContainer>
          )}

          {/* Orders bar chart */}
          {chartTab === 'orders' && (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={timeSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="ordersBarGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.4} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-default)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v: string) => v.slice(5)}
                  tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                  axisLine={{ stroke: 'var(--color-border-default)' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                  axisLine={false}
                  tickLine={false}
                  width={48}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: 'var(--color-bg-hover)', opacity: 0.4 }}
                  contentStyle={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border-default)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelFormatter={(label: string) => label}
                  formatter={(value: number) => [value, t('dashboard.orders')]}
                />
                <Bar
                  dataKey="orders"
                  fill="url(#ordersBarGradient)"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={48}
                />
              </BarChart>
            </ResponsiveContainer>
          )}

          {/* Visits area chart */}
          {chartTab === 'visits' && hasVisitsData && (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={timeSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="visitsAreaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-default)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v: string) => v.slice(5)}
                  tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                  axisLine={{ stroke: 'var(--color-border-default)' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                  axisLine={false}
                  tickLine={false}
                  width={48}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ strokeDasharray: '3 3', stroke: 'var(--color-border-default)' }}
                  contentStyle={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border-default)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelFormatter={(label: string) => label}
                  formatter={(value: number) => [value, t('analytics.visits', 'Visits')]}
                />
                <Area
                  type="monotone"
                  dataKey="visits"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  fill="url(#visitsAreaGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Top Products */}
        <div className="rounded-xl border border-border-default bg-bg-secondary p-5 shadow-sm">
          <h2 className="font-semibold mb-4">{t('analytics.topProducts')}</h2>
          {topProducts.length === 0 ? (
            <p className="text-sm text-text-muted py-8 text-center">{t('common.noData')}</p>
          ) : (
            <div className="space-y-3">
              {topProducts.slice(0, 10).map((product, i) => {
                const rankClass = i === 0 ? 'bg-yellow-500/20 text-yellow-500' : i === 1 ? 'bg-gray-300/20 text-gray-400' : i === 2 ? 'bg-orange-400/20 text-orange-400' : 'bg-bg-tertiary text-text-muted';
                return (
                <div key={product.productId} className="flex items-center justify-between py-2 border-b border-border-default/50 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${rankClass}`}>
                      {i + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium">{product.title}</p>
                      <p className="text-xs text-text-muted">{product.sales} {t('analytics.sales')}</p>
                    </div>
                  </div>
                  <span className="text-sm font-medium text-accent">
                    <CurrencyDisplay amount={product.revenue} currency={currentStore?.currency} />
                  </span>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Customers */}
        <div className="rounded-xl border border-border-default bg-bg-secondary p-5 shadow-sm">
          <h2 className="font-semibold mb-4">{t('analytics.topCustomers')}</h2>
          {topCustomers.length === 0 ? (
            <p className="text-sm text-text-muted py-8 text-center">{t('common.noData')}</p>
          ) : (
            <div className="space-y-3">
              {topCustomers.slice(0, 10).map((customer, i) => (
                <div key={customer.customerId} className="flex items-center justify-between py-2 border-b border-border-default/50 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-bg-tertiary text-xs font-medium">
                      {i + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium">{customer.name}</p>
                      {customer.email && <p className="text-xs text-text-muted">{customer.email}</p>}
                    </div>
                  </div>
                  <div className="text-end">
                    <p className="text-sm font-medium text-accent">
                      <CurrencyDisplay amount={customer.revenue} currency={currentStore?.currency} />
                    </p>
                    <p className="text-xs text-text-muted">{customer.orders} {t('dashboard.orders')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* New vs Returning Customers */}
        <div className="rounded-xl border border-border-default bg-bg-secondary p-5 shadow-sm lg:col-span-2">
          <h2 className="font-semibold mb-4">{t('analytics.customerBreakdown', 'Customer Breakdown')}</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {/* Total */}
            <div className="flex items-center gap-3 rounded-lg border border-border-default bg-bg-primary p-4">
              <div className="rounded-lg bg-bg-tertiary p-2.5">
                <Users className="h-5 w-5 text-text-secondary" />
              </div>
              <div>
                <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                  {t('analytics.totalCustomers', 'Total Customers')}
                </p>
                <p className="mt-1 text-2xl font-bold">{customers.total}</p>
              </div>
            </div>
            {/* New */}
            <div className="flex items-center gap-3 rounded-lg border border-success/30 bg-success/5 p-4">
              <div className="rounded-lg bg-success/15 p-2.5">
                <UserPlus className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="text-xs font-semibold text-success uppercase tracking-wider">
                  {t('analytics.newCustomers', 'New Customers')}
                </p>
                <p className="mt-1 text-2xl font-bold">{customers.new}</p>
                <p className="text-xs text-text-muted">
                  {customers.total > 0
                    ? `${Math.round((customers.new / customers.total) * 100)}%`
                    : '0%'}{' '}
                  {t('analytics.ofTotal', 'of total')}
                </p>
              </div>
            </div>
            {/* Returning */}
            <div className="flex items-center gap-3 rounded-lg border border-info/30 bg-info/5 p-4">
              <div className="rounded-lg bg-info/15 p-2.5">
                <UserCog className="h-5 w-5 text-info" />
              </div>
              <div>
                <p className="text-xs font-semibold text-info uppercase tracking-wider">
                  {t('analytics.returningCustomers', 'Returning Customers')}
                </p>
                <p className="mt-1 text-2xl font-bold">{customers.returning}</p>
                <p className="text-xs text-text-muted">
                  {customers.total > 0
                    ? `${Math.round((customers.returning / customers.total) * 100)}%`
                    : '0%'}{' '}
                  {t('analytics.ofTotal', 'of total')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="rounded-xl border border-border-default bg-bg-secondary p-5 shadow-sm lg:col-span-2">
          <h2 className="font-semibold mb-4">{t('analytics.categoryBreakdown')}</h2>
          {categoryBreakdown.length === 0 ? (
            <p className="text-sm text-text-muted py-8 text-center">{t('common.noData')}</p>
          ) : (
            <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
              {categoryBreakdown.map((cat) => {
                const totalRevenue = categoryBreakdown.reduce((s, c) => s + c.revenue, 0);
                const pct = totalRevenue > 0 ? Math.round((cat.revenue / totalRevenue) * 100) : 0;
                return (
                  <div key={cat.category} className="py-2 border-b border-border-default/50 last:border-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{cat.category}</span>
                      <span className="text-sm text-accent"><CurrencyDisplay amount={cat.revenue} currency={currentStore?.currency} /></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 flex-1 rounded-full bg-bg-tertiary overflow-hidden">
                        <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-text-muted w-8 text-end">{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Revenue by Payment Method */}
        {paymentMethodBreakdown.length > 0 && (
          <div className="rounded-xl border border-border-default bg-bg-secondary p-5 shadow-sm lg:col-span-2">
            <h2 className="font-semibold mb-4">{t('analytics.revenueByPaymentMethod')}</h2>
            <div className="space-y-3">
              {(() => {
                const maxMethodRevenue = Math.max(...paymentMethodBreakdown.map(pm => pm.revenue), 1);
                return paymentMethodBreakdown.map((pm) => (
                  <div key={pm.method} className="py-2">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium">{t(`analytics.paymentMethod.${pm.method}`, pm.method)}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-text-muted">{pm.count} {t('dashboard.orders')}</span>
                        <span className="text-sm font-medium text-accent">
                          <CurrencyDisplay amount={pm.revenue} currency={currentStore?.currency} />
                        </span>
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-bg-tertiary overflow-hidden">
                      <div
                        className="h-full rounded-full bg-accent/80 transition-all"
                        style={{ width: `${(pm.revenue / maxMethodRevenue) * 100}%` }}
                      />
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}

        {/* Order Status Distribution */}
        {orderStatusBreakdown.length > 0 && (
          <div className="rounded-xl border border-border-default bg-bg-secondary p-5 shadow-sm lg:col-span-2">
            <h2 className="font-semibold mb-4">{t('analytics.orderStatusDistribution')}</h2>
            <div className="flex flex-wrap gap-3">
              {orderStatusBreakdown.map((os) => (
                <span
                  key={os.status}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${STATUS_COLORS[os.status] ?? 'bg-bg-tertiary text-text-secondary'}`}
                >
                  {t(`orders.status.${os.status}`, os.status)}
                  <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-xs font-bold leading-none">
                    {os.count}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Conversion Funnel */}
      {conversionFunnel && (
        <div className="rounded-xl border border-border-default bg-bg-secondary p-5 shadow-sm mt-6">
          <h2 className="font-semibold mb-4">{t('analytics.conversionFunnel', 'Conversion Funnel')}</h2>
          <div className="space-y-3">
            {(() => {
              const steps = [
                { key: 'visits', label: t('analytics.funnelVisits', 'Visits'), value: conversionFunnel.visits, color: 'bg-blue-500' },
                { key: 'productViews', label: t('analytics.funnelProductViews', 'Product Views'), value: conversionFunnel.productViews, color: 'bg-purple-500' },
                { key: 'addToCart', label: t('analytics.funnelAddToCart', 'Add to Cart'), value: conversionFunnel.addToCart, color: 'bg-orange-500' },
                { key: 'checkoutStarted', label: t('analytics.funnelCheckout', 'Checkout Started'), value: conversionFunnel.checkoutStarted, color: 'bg-pink-500' },
                { key: 'orderCompleted', label: t('analytics.funnelOrder', 'Order Completed'), value: conversionFunnel.orderCompleted, color: 'bg-green-500' },
              ];
              const maxVal = Math.max(steps[0].value, 1);
              return steps.map((step, i) => {
                const pct = Math.round((step.value / maxVal) * 100);
                const prevVal = i > 0 ? steps[i - 1].value : null;
                const dropPct = prevVal && prevVal > 0 ? Math.round(((prevVal - step.value) / prevVal) * 100) : null;
                return (
                  <div key={step.key} className="flex items-center gap-3">
                    <span className="w-28 text-sm text-text-secondary shrink-0 text-end">{step.label}</span>
                    <div className="flex-1 h-7 rounded bg-bg-tertiary overflow-hidden">
                      <div className={`h-full rounded ${step.color} transition-all flex items-center px-2`} style={{ width: `${Math.max(pct, 2)}%` }}>
                        <span className="text-xs text-white font-medium">{step.value.toLocaleString()}</span>
                      </div>
                    </div>
                    {dropPct != null && dropPct > 0 && (
                      <span className="text-xs text-danger w-12 shrink-0">-{dropPct}%</span>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Traffic Sources + Device Breakdown */}
      {(trafficSources.length > 0 || deviceBreakdown.length > 0) && (
        <div className="grid gap-6 lg:grid-cols-2 mt-6">
          {/* Traffic Sources */}
          {trafficSources.length > 0 && (
            <div className="rounded-xl border border-border-default bg-bg-secondary p-5 shadow-sm">
              <h2 className="font-semibold mb-4">{t('analytics.trafficSources', 'Traffic Sources')}</h2>
              <div className="space-y-3">
                {trafficSources.slice(0, 8).map((src) => (
                  <div key={src.source}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm">{src.source}</span>
                      <span className="text-xs text-text-muted">{src.visits.toLocaleString()}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-bg-tertiary overflow-hidden">
                      <div className="h-full rounded-full bg-accent" style={{ width: `${src.percentage}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Device Breakdown */}
          {deviceBreakdown.length > 0 && (
            <div className="rounded-xl border border-border-default bg-bg-secondary p-5 shadow-sm">
              <h2 className="font-semibold mb-4">{t('analytics.deviceBreakdown', 'Devices')}</h2>
              {/* Stacked bar */}
              <div className="h-6 rounded-full overflow-hidden flex mb-4">
                {deviceBreakdown.map((d) => {
                  const color = d.device === 'mobile' ? 'bg-blue-500' : d.device === 'desktop' ? 'bg-green-500' : 'bg-orange-400';
                  return (
                    <div key={d.device} className={`${color} transition-all`} style={{ width: `${d.percentage}%` }} title={`${d.device}: ${d.percentage}%`} />
                  );
                })}
              </div>
              {/* Legend */}
              <div className="flex flex-wrap gap-4">
                {deviceBreakdown.map((d) => {
                  const Icon = d.device === 'mobile' ? Smartphone : d.device === 'desktop' ? Monitor : Bot;
                  const dotColor = d.device === 'mobile' ? 'bg-blue-500' : d.device === 'desktop' ? 'bg-green-500' : 'bg-orange-400';
                  return (
                    <div key={d.device} className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${dotColor}`} />
                      <Icon className="h-4 w-4 text-text-muted" />
                      <span className="text-sm">{t(`analytics.device.${d.device}`, d.device)}</span>
                      <span className="text-xs text-text-muted">{d.count.toLocaleString()} ({d.percentage}%)</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Country Breakdown */}
      {countryBreakdown.length > 0 && (
        <div className="rounded-xl border border-border-default bg-bg-secondary p-5 shadow-sm mt-6">
          <h2 className="flex items-center gap-2 font-semibold mb-4">
            <Globe className="h-5 w-5 text-accent" />
            {t('analytics.countryBreakdown', 'Visitors by Country')}
          </h2>
          <div className="space-y-3">
            {(() => {
              const maxVisits = Math.max(...countryBreakdown.map(c => c.visits), 1);
              return countryBreakdown.slice(0, 10).map((country, i) => (
                <div key={country.country} className="flex items-center gap-3">
                  <span className="w-6 text-center text-xs font-bold text-text-muted">{i + 1}</span>
                  <span className="w-28 text-sm font-medium truncate">{formatCountryName(country.country, i18n.language)}</span>
                  <div className="flex-1 h-2 rounded-full bg-bg-tertiary overflow-hidden">
                    <div className="h-full rounded-full bg-accent/70" style={{ width: `${(country.visits / maxVisits) * 100}%` }} />
                  </div>
                  <span className="text-xs text-text-muted w-16 text-end">{country.visits.toLocaleString()}</span>
                  {country.sessions != null && (
                    <span className="text-xs text-text-muted w-16 text-end">{country.sessions.toLocaleString()} {t('analytics.sessions', 'sessions')}</span>
                  )}
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* Top Pages */}
      <div className="rounded-xl border border-border-default bg-bg-secondary p-5 shadow-sm mt-6">
        <h2 className="flex items-center gap-2 font-semibold mb-4">
          <FileText className="h-5 w-5 text-accent" />
          {t('analytics.topPages', 'Top Pages')}
        </h2>
        {topPages.length === 0 ? (
          <p className="text-sm text-text-muted py-8 text-center">
            {t('analytics.noPageData', 'No page data available')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-default text-text-muted">
                  <th className="py-2 pe-4 text-start font-medium">{t('analytics.pagePath', 'Page')}</th>
                  <th className="py-2 px-4 text-end font-medium">{t('analytics.views', 'Views')}</th>
                  <th className="py-2 ps-4 text-end font-medium">{t('analytics.avgTime', 'Avg. Time')}</th>
                </tr>
              </thead>
              <tbody>
                {topPages.slice(0, 10).map((page) => (
                  <tr key={page.path} className="border-b border-border-default/50 last:border-0">
                    <td className="py-2.5 pe-4">
                      <code className="text-xs bg-bg-tertiary px-1.5 py-0.5 rounded font-mono truncate max-w-[300px] inline-block align-middle">
                        {page.path}
                      </code>
                    </td>
                    <td className="py-2.5 px-4 text-end font-medium">{page.views.toLocaleString()}</td>
                    <td className="py-2.5 ps-4 text-end text-text-muted">
                      {page.avgTime != null ? (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {page.avgTime >= 60
                            ? `${Math.floor(page.avgTime / 60)}m ${Math.round(page.avgTime % 60)}s`
                            : `${Math.round(page.avgTime)}s`}
                        </span>
                      ) : (
                        <span className="text-text-muted">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick links to sub-pages */}
      <div className="mt-6 flex justify-center gap-3">
        <Button variant="secondary" onClick={() => navigate(`/${storeId}/analytics/live`)}>
          <Eye className="h-4 w-4" />
          {t('analytics.viewLiveVisitors', 'Live Visitors')}
        </Button>
        <Button variant="secondary" onClick={() => navigate(`/${storeId}/analytics/search`)}>
          <Search className="h-4 w-4" />
          {t('analytics.viewSearchAnalytics', 'Search Analytics')}
        </Button>
      </div>
    </div>
  );
}
